import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { ListsyncFooter } from '@/components/listsync/Footer';

const ListsyncTermsPage: React.FC = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const navLinkStyle: React.CSSProperties = {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1,
    letterSpacing: 0,
    textTransform: 'capitalize',
    color: '#1a1a1a',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    cursor: 'pointer'
  };

  return (
    <>
      <Head>
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@600&display=swap"
          rel="stylesheet"
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .listsync-nav a:hover { color: #4F46E5 !important; }
              html { scroll-behavior: smooth; }
            `
          }}
        />
        <title>Website T&C - Listsync</title>
        <meta
          name="description"
          content="Website Terms and Conditions of Use for Listsync"
        />
      </Head>
      <div
        style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#ffffff',
          minHeight: '100vh'
        }}
      >
        {/* Header */}
        <header
          style={{
            padding: isMobile ? '16px 20px' : '24px 60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 10,
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}
        >
          <Link
            href="/"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <Image
              src="/listsync/listsync-logo.svg"
              alt="Listsync Logo"
              width={isMobile ? 120 : 160}
              height={isMobile ? 41 : 55}
              style={{ height: 'auto' }}
            />
          </Link>
        </header>

        {/* Main Content */}
        <main
          style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#ffffff',
            minHeight: 'calc(100vh - 200px)',
            color: '#1a1a1a'
          }}
        >
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '60px 40px'
            }}
          >
            {/* Title Section */}
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h1
                style={{
                  fontSize: '42px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  letterSpacing: '-0.02em'
                }}
              >
                Website T&C
              </h1>
              <p
                style={{
                  fontSize: '20px',
                  color: '#4b5563',
                  marginBottom: '8px'
                }}
              >
                NESTFLO LIMITED, trading as Listsync
              </p>
              <p
                style={{
                  fontSize: '18px',
                  color: '#64748b',
                  marginBottom: '8px'
                }}
              >
                Website Terms and Conditions of Use
              </p>
              <p style={{ fontSize: '18px', color: '#64748b' }}>Dec 2025</p>
            </div>

            {/* Content */}
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                1. Introduction
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  These terms and conditions apply between you, the User of
                  https://listsync.co.uk/ (including any sub-domains, unless
                  expressly excluded by their own terms and conditions –
                  &ldquo;Website&rdquo;), and the owner and operator of this
                  Website NESTFLO LIMITED, a limited company registered in
                  England and Wales, company number 16878995 and registered
                  address at Filwood Green Business Park, 1 Filwood Park Ln,
                  Bristol, England, BS4 1ET.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Please read these terms and conditions carefully, as they
                  affect your legal rights.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  If you have any comments or questions to these terms and
                  conditions, please contact us by email: hello@listsync.co.uk
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Your agreement to comply with and be bound by these terms and
                  conditions is deemed to occur upon your first use of the
                  Website. If you do not agree to be bound by these terms and
                  conditions, you should stop using the Website immediately.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  In these terms and conditions, User or Users means any third
                  party that accesses the Website and is not either (i) employed
                  by NESTFLO LIMITED and acting in the course of their
                  employment or (ii) engaged as a consultant or otherwise
                  providing services to NESTFLO LIMITED and accessing the
                  Website in connection with the provision of such services.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You must be at least 18 years of age to use this Website. By
                  using the Website and agreeing to these terms and conditions,
                  you represent and warrant that you are at least 18 years of
                  age.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                2. Intellectual property and acceptable use
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  In these terms and conditions, Content means any text,
                  graphics, images, audio, video, software, data compilations,
                  page layout, underlying code and software and any other form
                  of information capable of being stored in a computer that
                  appears on or forms part of this Website, including any such
                  content uploaded by Users.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  All Content included on the Website, unless uploaded by Users,
                  is the property of NESTFLO LIMITED, our affiliates or other
                  relevant third parties. By continuing to use the Website you
                  acknowledge that such Content is protected by copyright,
                  trademarks, database rights and other intellectual property
                  rights.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Nothing on this Website shall be construed as granting, by
                  implication, estoppel, or otherwise, any license or right to
                  use any trademark, logo or service mark displayed on this
                  Website without the owner&rsquo;s prior written permission.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You may, for your own personal, non-commercial use only, do
                  the following:
                  <br />
                  • retrieve, display and view the Content on a device;
                  <br />
                  • download and store the Content in electronic form on a disk
                  (but not on any server or other storage device connected to a
                  network); and
                  <br />• print one copy of the Content.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You must not otherwise reproduce, modify, copy, distribute or
                  use for commercial purposes any Content without the written
                  permission of NESTFLO LIMITED.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You acknowledge that you are responsible for any Content you
                  may submit via the Website, including the legality,
                  reliability, appropriateness, originality and copyright of any
                  such Content.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You may not upload to, distribute or otherwise publish through
                  the Website any Content that (i) is confidential, proprietary,
                  false, fraudulent, libellous, defamatory, obscene,
                  threatening, invasive of privacy or publicity rights,
                  infringing on intellectual property rights, abusive, illegal
                  or otherwise objectionable; (ii) may constitute or encourage a
                  criminal offence, violate the rights of any party or otherwise
                  give rise to liability or violate any law; or (iii) may
                  contain software viruses, political campaigning, chain
                  letters, mass mailings, or any form of &ldquo;spam.&rdquo;
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You may not use a false email address or other identifying
                  information, impersonate any person or entity or otherwise
                  mislead as to the origin of any content.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You may not upload commercial content onto the Website.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You represent and warrant that you own or otherwise control
                  all the rights to the Content you post; that the Content is
                  accurate; that use of the Content you supply does not violate
                  any provision of these terms and conditions and will not cause
                  injury to any person; and that you will indemnify NESTFLO
                  LIMITED for all claims resulting from Content you supply.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                3. Prohibited use
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  You may not use the Website for any of the following purposes:
                  <br />
                  • in any way which causes, or may cause, damage to the Website
                  or interferes with any other person&rsquo;s use or enjoyment
                  of the Website;
                  <br />
                  • in any way which is harmful, unlawful, illegal, abusive,
                  harassing, threatening or otherwise objectionable or in breach
                  of any applicable law, regulation, governmental order;
                  <br />• making, transmitting or storing electronic copies of
                  Content protected by copyright without the permission of the
                  owner.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                4. Links to other websites
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  This Website may contain links to other sites. Unless
                  expressly stated, these sites are not under the control of
                  NESTFLO LIMITED or that of our affiliates.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We assume no responsibility for the content of such Websites
                  and disclaim liability for any and all forms of loss or damage
                  arising out of the use of them.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  The inclusion of a link to another site on this Website does
                  not imply any endorsement of the sites themselves or of those
                  in control of them.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                5. Privacy Policy and Cookies Policy
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Use of the Website is also governed by our Privacy Policy and
                  Cookies Policy, which are incorporated into these terms and
                  conditions by this reference.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                6. Availability of the Website and disclaimers
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Any online facilities, tools, services or information that
                  NESTFLO LIMITED makes available through the Website (the
                  Service) is provided &ldquo;as is&rdquo; and on an &ldquo;as
                  available&rdquo; basis. We give no warranty that the Service
                  will be free of defects and/or faults. To the maximum extent
                  permitted by the law, we provide no warranties (express or
                  implied) of fitness for a particular purpose, accuracy of
                  information, compatibility and satisfactory quality. NESTFLO
                  LIMITED is under no obligation to update information on the
                  Website.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Whilst NESTFLO LIMITED uses reasonable endeavours to ensure
                  that the Website is secure and free of errors, viruses and
                  other malware, we give no warranty or guaranty in that regard
                  and all Users take responsibility for their own security, that
                  of their personal details and their computers.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  NESTFLO LIMITED accepts no liability for any disruption or
                  non-availability of the Website.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  NESTFLO LIMITED reserves the right to alter, suspend or
                  discontinue any part (or the whole) of the Website including,
                  but not limited to, any products and/or services available.
                  These terms and conditions shall continue to apply to any
                  modified version of the Website unless it is expressly stated
                  otherwise.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                7. Limitation of liability
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Nothing in these terms and conditions will: (a) limit or
                  exclude our or your liability for death or personal injury
                  resulting from our or your negligence, as applicable; (b)
                  limit or exclude our or your liability for fraud or fraudulent
                  misrepresentation; or (c) limit or exclude any of our or your
                  liabilities in any way that is not permitted under applicable
                  law.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We will not be liable to you in respect of any losses arising
                  out of events beyond our reasonable control.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  To the maximum extent permitted by law, NESTFLO LIMITED
                  accepts no liability for any of the following:
                  <br />
                  • any business losses, such as loss of profits, income,
                  revenue, anticipated savings, business, contracts, goodwill or
                  commercial opportunities;
                  <br />
                  • loss or corruption of any data, database or software;
                  <br />• any special, indirect or consequential loss or damage.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                8. General
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  You may not transfer any of your rights under these terms and
                  conditions to any other person. We may transfer our rights
                  under these terms and conditions where we reasonably believe
                  your rights will not be affected.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  These terms and conditions may be varied by us from time to
                  time. Such revised terms will apply to the Website from the
                  date of publication. Users should check the terms and
                  conditions regularly to ensure familiarity with the then
                  current version.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  These terms and conditions, together with the Privacy Policy
                  and Cookies Policy, contain the whole agreement between the
                  parties relating to its subject matter and supersede all prior
                  discussions, arrangements or agreements that might have taken
                  place in relation to the terms and conditions.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  The Contracts (Rights of Third Parties) Act 1999 shall not
                  apply to these terms and conditions and no third party will
                  have any right to enforce or rely on any provision of these
                  terms and conditions.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  If any court or competent authority finds that any provision
                  of these terms and conditions (or part of any provision) is
                  invalid, illegal or unenforceable, that provision or
                  part-provision will, to the extent required, be deemed to be
                  deleted, and the validity and enforceability of the other
                  provisions of these terms and conditions will not be affected.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Unless otherwise agreed, no delay, act or omission by a party
                  in exercising any right or remedy will be deemed a waiver of
                  that, or any other, right or remedy.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  This Agreement shall be governed by and interpreted according
                  to the law of England and Wales and all disputes arising under
                  the Agreement (including non-contractual disputes or claims)
                  shall be subject to the exclusive jurisdiction of the English
                  and Welsh courts.
                </p>
              </div>
            </div>
          </div>
        </main>

        <ListsyncFooter />
      </div>
    </>
  );
};

export default ListsyncTermsPage;
